/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable, OnDestroy } from '@angular/core';
import { CallState } from '@lcs/shared/api';
import { Action, Store, select } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { AppState } from 'src/app/app.reducer';
import { Reports } from '../reports.enum';
import { ReportState } from '../state/models/report-state';
import * as ReportActions from '../state/report.actions';
import { ReportParametersProp } from '../state/report.actions';
import * as fromReports from '../state/reports.reducer';

@Injectable({ providedIn: 'root' })
export class ReportService<P extends { viewMode?: any }, R> implements OnDestroy {
   allowExcel$: Observable<boolean>;

   allowHtml$: Observable<boolean>;

   allowPdf$: Observable<boolean>;

   allowMemorize$: Observable<boolean>;

   callState$: Observable<CallState>;

   data$: Observable<R | null>;

   reportName$: Observable<string>;

   showParametersDialog$ = new BehaviorSubject<boolean>(false);

   lastRunParameters: ReportParametersProp<P> | null = null;

   destroyed$ = new Subject<void>();

   constructor(private store: Store<AppState>) {
      this.allowExcel$ = store.pipe(select(fromReports.selectCurrentReportAllowExcel));
      this.allowHtml$ = store.pipe(select(fromReports.selectCurrentReportAllowHtml));
      this.allowPdf$ = store.pipe(select(fromReports.selectCurrentReportAllowPdf));
      this.allowMemorize$ = store.pipe(select(fromReports.selectCurrentReportAllowMemorize));
      this.callState$ = store.pipe(select(fromReports.selectCurrentReportCallState));
      this.data$ = store.pipe(select(fromReports.selectCurrentReportData));
      this.reportName$ = store.pipe(select(fromReports.selectCurrentReportName));
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onDownloadExcelClick(): void {
      this.dispatch(
         (id) => {
            return ReportActions.downloadExcel({ id });
         },
         (state) => {
            return state.allowExcel;
         }
      );
   }

   onDownloadPdfClick(): void {
      this.dispatch(
         (id) => {
            return ReportActions.downloadPdf({ id });
         },
         (state) => {
            return state.allowPdf;
         }
      );
   }

   onDownloadHtmlClick(): void {
      this.dispatch(
         (id) => {
            return ReportActions.downloadHtml({ id });
         },
         (state) => {
            return state.allowHtml;
         }
      );
   }

   onSubmit(rawFormValue: any, reportParameters: P): void {
      this.lastRunParameters = { rawFormValue, reportParameters };
      this.dispatch((id) => {
         return ReportActions.reportParametersSubmitted({ id, rawFormValue, reportParameters });
      });
   }

   onViewModeChanged(viewMode: string | null): void {
      this.dispatch((id) => {
         return ReportActions.viewModeChanged({ id, viewMode });
      });
   }

   saveReportParameters(name: string): void {
      if (this.lastRunParameters) {
         this.dispatch((id) => {
            return ReportActions.reportParameterSave({
               id,
               name,
            });
         });
      }
   }

   updateShowParametersDialog(value: boolean): void {
      this.showParametersDialog$.next(value);
   }

   private dispatch(build: (id: Reports) => Action, predicate?: (state: ReportState<P, R>) => boolean): void {
      this.store
         .pipe(
            select(fromReports.selectCurrentReport),
            take(1),
            filter((state: ReportState<P, R> | null): state is ReportState<P, R> => {
               return state != null && typeof predicate === 'function' ? predicate(state) : true;
            })
         )
         .subscribe((state: ReportState<P, R>) => {
            this.store.dispatch(build(state.id));
         });
   }
}
